import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {Button, Card, DatePicker, Flex, Form, Input, message, Modal, Select, Space, Table, Tag, Typography} from "antd";
import {DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined} from "@ant-design/icons";
import {createGroup, deleteGroup, getAllGroups} from "../../../../../store/slices/MicroqualificationGroupsSlice";
import {getAllMicroQualifications} from "../../../../../store/slices/MicroqualificationsSlice";
import {getAllTeachersPageable} from "../../../../../store/slices/TeachersListSlice";
import {
    changeCurrentPage
} from "../../../../../store/slices/tableController/MicroqualificationGroupsListTableController";
import IntlMessage from "../../../../../components/IntlMessage/IntlMessage";
import {LocalName} from "../../../../../utils/LocalName";
import {format} from "date-fns";

const {Title, Text} = Typography;
const {confirm} = Modal;

const GroupsList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
    const [creating, setCreating] = useState(false);

    const {groups, groupsLoading, groupsError} = useSelector(state => state.microqualificationGroups);
    const {currentPage, pageSize} = useSelector(state => state.microqualificationGroupsListTableController);
    const {
        microQualifications,
        isLoading: microQualificationsLoading
    } = useSelector(state => state.microQualificationList);
    const {teachers, isLoading: teachersLoading} = useSelector(state => state.teachersList);

    useEffect(() => {
        dispatch(getAllGroups());
        dispatch(getAllMicroQualifications({page: 1, limit: 1000, role: 'admin'}));
        dispatch(getAllTeachersPageable({page: 1, limit: 1000}));
    }, [dispatch]);

    useEffect(() => {
        if (groupsError) {
            message.error(<IntlMessage id="mqGroups.failedToLoad"/>);
        }
    }, [groupsError]);

    const handleDelete = (groupId, groupName) => {
        confirm({
            title: <IntlMessage id="mqGroups.deleteGroup"/>,
            content: <IntlMessage id="mqGroups.confirmDelete" values={{name: groupName}}/>,
            okText: <IntlMessage id="delete"/>,
            okType: 'danger',
            cancelText: <IntlMessage id="cancel"/>,
            onOk: async () => {
                try {
                    await dispatch(deleteGroup(groupId)).unwrap();
                    message.success(<IntlMessage id="mqGroups.groupDeleted"/>);
                } catch (error) {
                    message.error(<IntlMessage id="mqGroups.failedToDelete"/>);
                }
            },
        });
    };

    const handleCreateGroup = () => {
        form.resetFields();
        setIsCreateModalVisible(true);
    };

    const generateAcademicYears = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let i = -2; i <= 2; i++) {
            const startYear = currentYear + i;
            const endYear = startYear + 1;
            years.push({
                value: `${startYear}-${endYear}`,
                label: `${startYear}-${endYear}`
            });
        }
        return years;
    };

    const onFinishCreate = async (values) => {
        setCreating(true);
        try {
            const groupData = {
                name: values.name,
                academicYear: values.academicYear,
                startDate: values.startDate.format('YYYY-MM-DD'),
                endDate: values.endDate.format('YYYY-MM-DD'),
                microqualificationId: values.microqualificationId,
                teacherId: values.teacherId
            };

            const result = await dispatch(createGroup(groupData)).unwrap();
            message.success(<IntlMessage id="mqGroups.groupCreated"/>);
            setIsCreateModalVisible(false);
            form.resetFields();
            navigate(`/p/microqualification-groups/${result.id}`);
        } catch (error) {
            message.error(error?.message || <IntlMessage id="mqGroups.failedToCreate"/>);
        } finally {
            setCreating(false);
        }
    };

    const handleTableChange = (pagination) => {
        dispatch(changeCurrentPage({
            page: pagination.current,
            pageSize: pagination.pageSize
        }));
    };

    const columns = [
        {
            title: <Text strong><IntlMessage id="mqGroups.groupName"/></Text>,
            dataIndex: 'name',
            key: 'name',
            width: '15%',
            render: (text) => <Text>{text}</Text>
        },
        {
            title: <Text strong><IntlMessage id="mqGroups.microqualification"/></Text>,
            dataIndex: 'nameEng',
            key: 'nameEng',
            width: '20%',
            render: (nameEng) => (
                <div>
                    <Text>{nameEng}</Text>
                </div>
            )
        },
        {
            title: <Text strong><IntlMessage id="mqGroups.academicYear"/></Text>,
            dataIndex: 'academicYear',
            key: 'academicYear',
            width: '12%',
            align: 'center',
            render: (text) => <Text>{text}</Text>
        },
        {
            title: <Text strong><IntlMessage id="mqGroups.period"/></Text>,
            key: 'period',
            width: '18%',
            render: (_, record) => (
                <div>
                    <Text style={{fontSize: 12}}>
                        {format(new Date(record.startDate), 'dd.MM.yyyy')} - {format(new Date(record.endDate), 'dd.MM.yyyy')}
                    </Text>
                </div>
            )
        },
        {
            title: <Text strong><IntlMessage id="mqGroups.teacher"/></Text>,
            dataIndex: 'teacherName',
            key: 'teacherName',
            width: '15%',
            render: (teacherName) => (
                <Text>{teacherName}</Text>
            )
        },
        {
            title: <Text strong><IntlMessage id="mqGroups.students"/></Text>,
            dataIndex: 'studentCount',
            key: 'studentCount',
            width: '10%',
            align: 'center',
            render: (count) => (
                <Tag color="blue">{count || 0}</Tag>
            )
        },
        {
            title: <Text strong><IntlMessage id="mqGroups.actions"/></Text>,
            key: 'actions',
            width: '10%',
            align: 'center',
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="text"
                        icon={<EyeOutlined/>}
                        onClick={() => navigate(`/p/microqualification-groups/${record.id}`)}
                        title="View Details"
                    />
                    <Button
                        type="text"
                        icon={<EditOutlined/>}
                        onClick={() => navigate(`/p/microqualification-groups/${record.id}/edit`)}
                        title="Edit"
                    />
                    <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined/>}
                        onClick={() => handleDelete(record.id, record.name)}
                        title="Delete"
                    />
                </Space>
            )
        }
    ];

    return (
        <div style={{width: '100%', height: '100%'}}>
            <Card bordered={false} style={{height: '100%'}}>
                <Flex justify="space-between" align="center" style={{marginBottom: 16}}>
                    <Title level={3} style={{margin: 0}}>
                        <IntlMessage id="mqGroups.title"/>
                    </Title>
                    <Button
                        type="primary"
                        icon={<PlusOutlined/>}
                        onClick={handleCreateGroup}
                        size="large"
                    >
                        <IntlMessage id="mqGroups.createGroup"/>
                    </Button>
                </Flex>

                <Table
                    columns={columns}
                    dataSource={groups}
                    loading={groupsLoading}
                    rowKey="id"
                    scroll={{x: 1200}}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        showSizeChanger: true,
                        showTotal: (total) => <IntlMessage id="mqGroups.total" values={{total}}/>,
                        pageSizeOptions: ['5', '10', '20', '50'],
                    }}
                    onChange={handleTableChange}
                />
            </Card>

            <Modal
                title={<IntlMessage id="mqGroups.createNewGroup"/>}
                open={isCreateModalVisible}
                onCancel={() => {
                    setIsCreateModalVisible(false);
                    form.resetFields();
                }}
                footer={null}
                width={700}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinishCreate}
                    style={{marginTop: 24}}
                >
                    <Form.Item
                        label={<IntlMessage id="mqGroups.groupName"/>}
                        name="name"
                        rules={[
                            {required: true, message: <IntlMessage id="mqGroups.groupNameRequired"/>},
                            {max: 100, message: <IntlMessage id="mqGroups.groupNameMax"/>}
                        ]}
                    >
                        <Input placeholder="e.g., CFA-2024-Fall" size="large"/>
                    </Form.Item>

                    <Form.Item
                        label={<IntlMessage id="mqGroups.microqualification"/>}
                        name="microqualificationId"
                        rules={[{required: true, message: <IntlMessage id="mqGroups.microqualificationRequired"/>}]}
                    >
                        <Select
                            placeholder={<IntlMessage id="mqGroups.selectMicroqualification"/>}
                            size="large"
                            loading={microQualificationsLoading}
                            showSearch
                            filterOption={(input, option) =>
                                (option?.label?.toLowerCase() ?? '').includes(input.toLowerCase())
                            }
                        >
                            {microQualifications?.map(mq => (
                                <Select.Option
                                    key={mq.id}
                                    value={mq.id}
                                    label={LocalName.getName(mq.name)}
                                >
                                    {LocalName.getName(mq.name)}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={<IntlMessage id="mqGroups.teacher"/>}
                        name="teacherId"
                        rules={[{required: true, message: <IntlMessage id="mqGroups.teacherRequired"/>}]}
                    >
                        <Select
                            placeholder={<IntlMessage id="mqGroups.selectTeacher"/>}
                            size="large"
                            loading={teachersLoading}
                            showSearch
                            filterOption={(input, option) =>
                                (option?.label?.toLowerCase() ?? '').includes(input.toLowerCase())
                            }
                        >
                            {teachers?.map(teacher => (
                                <Select.Option
                                    key={teacher.id}
                                    value={teacher.id}
                                    label={`${teacher.firstName} ${teacher.lastName}`}
                                >
                                    <div>
                                        <div>{teacher.firstName} {teacher.lastName}</div>
                                        <div style={{fontSize: 12, color: '#666'}}>
                                            {teacher.email}
                                        </div>
                                    </div>
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={<IntlMessage id="mqGroups.academicYear"/>}
                        name="academicYear"
                        rules={[{required: true, message: <IntlMessage id="mqGroups.academicYearRequired"/>}]}
                    >
                        <Select
                            placeholder={<IntlMessage id="mqGroups.selectAcademicYear"/>}
                            size="large"
                            options={generateAcademicYears()}
                        />
                    </Form.Item>

                    <Form.Item
                        label={<IntlMessage id="mqGroups.startDate"/>}
                        name="startDate"
                        rules={[{required: true, message: <IntlMessage id="mqGroups.startDateRequired"/>}]}
                    >
                        <DatePicker
                            style={{width: '100%'}}
                            size="large"
                            format="DD.MM.YYYY"
                            placeholder={""}
                        />
                    </Form.Item>

                    <Form.Item
                        label={<IntlMessage id="mqGroups.endDate"/>}
                        name="endDate"
                        rules={[
                            {required: true, message: <IntlMessage id="mqGroups.endDateRequired"/>},
                            ({getFieldValue}) => ({
                                validator(_, value) {
                                    const startDate = getFieldValue('startDate');
                                    if (!value || !startDate || value.isAfter(startDate)) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(<IntlMessage id="mqGroups.endDateMustBeAfter"/>));
                                },
                            }),
                        ]}
                    >
                        <DatePicker
                            style={{width: '100%'}}
                            size="large"
                            format="DD.MM.YYYY"
                            placeholder={""}
                        />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={creating}
                                size="large"
                            >
                                <IntlMessage id="mqGroups.createGroup"/>
                            </Button>
                            <Button
                                onClick={() => {
                                    setIsCreateModalVisible(false);
                                    form.resetFields();
                                }}
                                size="large"
                            >
                                <IntlMessage id="mqGroups.cancel"/>
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default GroupsList;
